/**
 * Outdoor field builder (+field): meshes a field / town DungeonDef (`d.field`) from the pure
 * layout (outdoor/layout.ts). Draws:
 *  - terrain: chunked height-field grid, painted grass / dirt paths / tree shade in vertex colours
 *    over a canvas grass-detail texture; the walkable ground is flat at y = 0, hills rise beyond;
 *  - town cobbles: a soft-edged overlay on the square (canvas cobble texture, vertex alpha);
 *  - props: every placement merged per 32 yd chunk on the prop atlas (one draw + one outline hull
 *    per chunk; small decor in a separate detail mesh, hidden on the low preset);
 *  - far trees: instanced low-poly trees out to the margin (no void past the edge band);
 *  - grass: instanced swaying tufts per chunk (wind + the level-up shockwave push, GRASS_SHOCK);
 *  - sky dome with soft clouds, flames / glows for lanterns, campfires, the forge and crystals;
 *  - portals (outdoor/portals.ts).
 * It also installs the terrain height (ground.ts) so units, camera and picking follow the hills.
 */
import { occludable, updateFieldOcclusion, OCC_U } from './occlude'; // +polish
import * as THREE from 'three';
import type { DungeonDef } from '@keystone/sim';
import { ATLAS_WHITE, type ToonAssets } from '../assets';
import { type Blocker, blockersFromWalkable } from '../camCollision';
import type { DungeonBuild } from '../dungeon';
import { setGroundFn } from '../ground';
import { type HullRegistry, NOISE_GLSL, U, addHull, addOutlineNormals, flameMaterial, ownedToonMaterial } from '../style';
import { type GlowSpec, glowCards, motes } from './glow';
import { type FieldLayout, type OutPlacement, layoutField, sampleGrid, vnoise } from './layout';
import { buildPortals } from './portals';
import { buildTowerLandmark } from './tower';
import { OUT, combine, outdoorProc, paint, xf } from './procs';
import { type OutdoorTheme, outdoorDungeonTheme } from './themes';
import { MOSSVALE_SHAFT_SPOTS, buildAtmosphere } from './atmosphere'; // +atmos (sky, clouds, distant ranges, far LOD, shafts)
import { RANGE_REACH } from './atmosphere/farWorld'; // +atmos
import { FAR_TREE_DRAW } from './atmosphere/farLod'; // +atmos
import { farSpruceGeometry } from './valley'; // +valley (also registers the valley procs)
import './props'; // +detail: broadleaf trees, ground cover, points of interest (registers their procs)
import { farBroadleafGeometry } from './props/trees'; // +detail
import { buildLife } from './life'; // +detail: ambient life (butterflies, birds, fish, critters, leaves, motes)
import { buildWater } from './water';
import { fieldTerrainOf } from '@keystone/content';

/** Level-up shockwave on the grass: (x, z, radius, strength) in three.js world space. */
export const GRASS_SHOCK = { value: new THREE.Vector4(0, 0, 0, 0) };

const CHUNK = 32;
/** +detail: natural props (trees, rocks, bushes) merge per streaming cell: the open-ground scatter puts some in nearly every chunk. */
const NATURE_CHUNK = 64;
/** +zone: draw distances (yd from the camera, plus the chunk's radius) for field meshes. */
const DRAW = { prop: 165, ground: 95, detail: 70, grass: 60, glow: 220 } as const; // +atmos: far LOD stand-ins (atmosphere/farLod.ts) grow in from 160
/** +zone: streaming cell size (yd) and radii: build within / drop beyond (distance to the cell's square). */
const STREAM_CELL = 64;
const STREAM_BUILD = DRAW.glow + 10;
const STREAM_DROP = STREAM_BUILD + 70;
const GRASS_BUILD = DRAW.grass + 30;
const GRASS_DROP = GRASS_BUILD + 50;
/** Cells within this distance build at once after a jump (arrival, waystone); the rest a few ms a frame. */
const STREAM_NOW = 90;
const STREAM_BUDGET_MS = 4;
/** The coarse far terrain's vertex spacing (yd). */
const FAR_GROUND_STEP = 5;
const EMPTY_ARRAY = new Float32Array(0);
const now = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const GRASS_TILE = 3.2;
const COBBLE_TILE = 4.5;

const lin = (hex: number): [number, number, number] => {
  const c = new THREE.Color(hex);
  return [c.r, c.g, c.b];
};

export interface FieldBuild extends DungeonBuild {
  layout: FieldLayout;
  /** Ambience emitters (the barrow hum). */
  hums: { pos: { x: number; y: number }; radius: number }[];
  /** Per-frame: sky follows the camera; portal labels fade in near the hero. */
  frame(camera: THREE.Camera, hero: { x: number; y: number } | null): void;
}

export function buildToonField(d: DungeonDef, theme: OutdoorTheme, assets: ToonAssets, hulls: HullRegistry, maxAniso: number): FieldBuild {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
  const L = layoutField(d, theme);
  const group = new THREE.Group();
  group.name = 'toon-field';
  const owned: { dispose(): void }[] = [];
  const detail: THREE.Object3D[] = [];
  let drawables = 0;
  const G = L.grid;
  // +zone: on a terrain map the walkable ground has relief too.
  const heightAt = (x: number, y: number): number => (L.terrain || sampleGrid(G, L.sdf, x, y) > 0 ? sampleGrid(G, L.height, x, y) : 0);
  // Units, camera, picking and fx all read the terrain height from here on.
  setGroundFn(heightAt);

  // ---- terrain ------------------------------------------------------------------------------
  const grassTex = makeGrassTexture(maxAniso);
  if (grassTex) owned.push(grassTex);
  const groundMat = ownedToonMaterial({ color: 0xffffff, map: grassTex, vertexColors: true });
  groundMat.name = 'field-ground';
  owned.push(groundMat);
  {
    // +scenery: painted cliff faces: steep terrain gets vertical strata, faint layering and a value
    // ramp from a cool, darker foot to a sunlit lip (vertex colours are too sparse on a cliff face).
    const gb = groundMat.onBeforeCompile;
    groundMat.onBeforeCompile = (sh, r) => {
      gb.call(groundMat, sh, r);
      sh.vertexShader = sh.vertexShader.replace('#include <common>', '#include <common>\nvarying vec3 vTW; varying vec3 vTN;').replace('#include <begin_vertex>', '#include <begin_vertex>\n  vTW = (modelMatrix * vec4(position, 1.0)).xyz; vTN = normal;');
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', `#include <common>\nvarying vec3 vTW; varying vec3 vTN;\n${NOISE_GLSL}`)
        .replace(
          '#include <color_fragment>',
          `#include <color_fragment>
          {
            float steep = smoothstep(0.42, 0.75, 1.0 - normalize(vTN).y);
            if (steep > 0.0) {
              vec2 hz = normalize(vTN.xz + vec2(1e-4));
              float along = dot(vTW.xz, vec2(-hz.y, hz.x));
              float strata = noise(vec2(along * 0.42, vTW.y * 0.08)) * 0.6 + noise(vec2(along * 1.4, vTW.y * 0.22)) * 0.4;
              float layers = noise(vec2(along * 0.06, vTW.y * 0.75));
              float grad = smoothstep(-2.0, 15.0, vTW.y);
              float crack = 1.0 - smoothstep(0.0, 0.045, abs(noise(vec2(along * 0.75, vTW.y * 0.045)) - 0.5)) * 0.42;
              vec3 rock = diffuseColor.rgb * (0.6 + 0.58 * strata + 0.14 * layers) * crack * mix(vec3(0.8, 0.88, 1.04), vec3(1.16, 1.13, 1.04), grad);
              diffuseColor.rgb = mix(diffuseColor.rgb, rock, steep);
            }
          }`,
        );
    };
    groundMat.customProgramCacheKey = () => 'toon-field-ground';
  }
  // +zone: the ground palette blends the biomes around a point (four samples a few yards apart).
  type V3 = [number, number, number];
  type Pal = { g0: V3; g1: V3; g2: V3; dirt: V3; shade: V3; sun: number; edge: number; warm: number; rock: number };
  const palCache = new Map<string, Pal>();
  const palOf = (lk: ReturnType<typeof L.look>): Pal => {
    let p = palCache.get(lk.id);
    if (!p) {
      const gr = lk.ground;
      p = { g0: lin(gr.grass[0]), g1: lin(gr.grass[1]), g2: lin(gr.grass[2]), dirt: lin(gr.dirt), shade: lin(gr.shade), sun: gr.sunPatches ?? 0, edge: gr.edgeDark ?? 0, warm: gr.warmth ?? 0, rock: gr.rock ?? 0 };
      palCache.set(lk.id, p);
    }
    return p;
  };
  const acc: Pal = { g0: [0, 0, 0], g1: [0, 0, 0], g2: [0, 0, 0], dirt: [0, 0, 0], shade: [0, 0, 0], sun: 0, edge: 0, warm: 0, rock: 0 };
  const paletteAt = (x: number, y: number): Pal => {
    for (const key of ['g0', 'g1', 'g2', 'dirt', 'shade'] as const) acc[key][0] = acc[key][1] = acc[key][2] = 0;
    acc.sun = acc.edge = acc.warm = acc.rock = 0;
    for (const [dx, dy] of [[-6, -6], [6, -6], [-6, 6], [6, 6]] as const) {
      const p = palOf(L.look(x + dx, y + dy));
      for (const key of ['g0', 'g1', 'g2', 'dirt', 'shade'] as const) for (let c = 0; c < 3; c++) acc[key][c]! += p[key][c]! * 0.25;
      acc.sun += p.sun * 0.25;
      acc.edge += p.edge * 0.25;
      acc.warm += p.warm * 0.25;
      acc.rock += p.rock * 0.25;
    }
    return acc;
  };
  const groundColor = (k: number, x: number, y: number): [number, number, number] => {
    const P = paletteAt(x, y);
    const g0 = P.g0;
    const g1 = P.g1;
    const g2 = P.g2;
    const dirt = P.dirt;
    const shadeC = P.shade;
    // Several scales (no big uniform blobs from above): broad, mid and small.
    const n1 = L.terrain ? 0.45 * vnoise(x * 0.04, y * 0.04) + 0.35 * vnoise(x * 0.12 + 5, y * 0.12 - 2) + 0.2 * vnoise(x * 0.31 + 1, y * 0.31 + 3) : vnoise(x * 0.06, y * 0.06);
    const n2 = L.terrain ? 0.5 * vnoise(x * 0.075 + 30, y * 0.075 - 12) + 0.5 * vnoise(x * 0.19 - 4, y * 0.19 + 8) : vnoise(x * 0.11 + 30, y * 0.11 - 12);
    const n3 = vnoise(x * 0.5, y * 0.5);
    let r = g0[0] + (g1[0] - g0[0]) * smooth01(0.45, 0.8, n1);
    let g = g0[1] + (g1[1] - g0[1]) * smooth01(0.45, 0.8, n1);
    let b = g0[2] + (g1[2] - g0[2]) * smooth01(0.45, 0.8, n1);
    const dk = smooth01(0.55, 0.85, n2) * 0.6;
    r += (g2[0] - r) * dk;
    g += (g2[1] - g) * dk;
    b += (g2[2] - b) * dk;
    const e = L.sdf[k]!;
    // Hills beyond the edge: a touch lighter / yellower on the tops (sunlit), shade under trees.
    const top = (L.sdf[k]! > 0 && !L.terrain ? Math.min(1, L.height[k]! / Math.max(1, theme.hills)) : 0) * 0.12;
    r *= 1 + top;
    g *= 1 + top * 0.8;
    const sh = L.shade[k]!;
    r += (shadeC[0] - r) * sh;
    g += (shadeC[1] - g) * sh;
    b += (shadeC[2] - b) * sh;
    // Worn dirt paths (and a dirt skirt under the town square's cobbles).
    const pw = Math.max(L.path[k]!, L.plaza[k]! * 0.9);
    const dj = 0.9 + 0.2 * n3;
    r += (dirt[0] * dj - r) * pw;
    g += (dirt[1] * dj - g) * pw;
    b += (dirt[2] * dj - b) * pw;
    // A soft darker rim just outside the walkable edge reads as the field's boundary.
    const rim = e > 0 ? 1 - 0.1 * smooth01(0.0, 2.5, e) * (1 - smooth01(4, 10, e)) : 1;
    // +valley: big soft sunlit patches (yellow-green) and a cool, darker rim toward the edge (value
    // framing: bright centre, dark edges under the conifers).
    const sp = P.sun;
    if (sp > 0) {
      const sun = smooth01(0.5, 0.8, 0.65 * vnoise(x * 0.028 + 91, y * 0.028 - 37) + 0.35 * vnoise(x * 0.09 + 3, y * 0.09)) * sp;
      r += (g1[0] * 1.08 - r) * sun * 0.45 * (1 - pw);
      g += (g1[1] * 1.06 - g) * sun * 0.45 * (1 - pw);
      b += (g1[2] * 0.9 - b) * sun * 0.45 * (1 - pw);
      // Cool blue-green patches between the sunlit ones (painted cloud shadow).
      const cool = smooth01(0.52, 0.8, 0.6 * vnoise(x * 0.034 - 13, y * 0.034 + 51) + 0.4 * vnoise(x * 0.1 + 7, y * 0.1 - 9)) * sp * 0.35 * (1 - sun);
      r += (g2[0] * 0.9 - r) * cool;
      g += (g2[1] * 0.95 - g) * cool;
      b += (g2[2] * 1.05 - b) * cool;
    }
    const ed = P.edge;
    if (ed > 0) {
      const k = smooth01(-18, 0, e) * ed;
      r += (g2[0] * 0.8 - r) * k;
      g += (g2[1] * 0.85 - g) * k;
      b += (g2[2] * 1.0 - b) * k;
    }
    // +zone: biome warmth (meadow light and warm, forest floor dark and cool), height (hilltops catch the sun), exposed stone.
    if (L.terrain) {
      const w = P.warm;
      const hk = 1 + Math.min(1, Math.max(0, L.height[k]! / 18)) * 0.08;
      r *= (1 + 0.07 * w) * hk;
      g *= (1 + 0.03 * w) * hk;
      b *= (1 - 0.07 * w) * (1 + (hk - 1) * 0.4);
      if (P.rock > 0) {
        const stone = smooth01(0.42, 0.62, 0.6 * vnoise(x * 0.09 + 21, y * 0.09 - 4) + 0.4 * n3) * P.rock * (1 - pw);
        const t = 0.8 + 0.35 * n2;
        r += (ROCK[0] * t - r) * stone;
        g += (ROCK[1] * t * 1.02 - g) * stone;
        b += (ROCK[2] * t - b) * stone;
      }
    }
    // +zone: steep ground is bare rock (the plateau's cliff faces, the hill's flanks), grey-blue with mossy streaks.
    if (L.terrain) {
      const i = k % G.nx;
      const j = (k - i) / G.nx;
      const hx = (L.height[j * G.nx + Math.min(G.nx - 1, i + 1)]! - L.height[j * G.nx + Math.max(0, i - 1)]!) / (2 * G.cell);
      const hy = (L.height[Math.min(G.ny - 1, j + 1) * G.nx + i]! - L.height[Math.max(0, j - 1) * G.nx + i]!) / (2 * G.cell);
      const rk = smooth01(0.55, 1.05, Math.hypot(hx, hy)) * (0.85 + 0.15 * n3);
      if (rk > 0) {
        const t = 0.5 + 0.5 * n2;
        r += (ROCK[0] * (0.8 + 0.35 * t) - r) * rk;
        g += (ROCK[1] * (0.8 + 0.35 * t) - g) * rk;
        b += (ROCK[2] * (0.8 + 0.35 * t) - b) * rk;
      }
    }
    // Value variation (painted look).
    const v = (0.94 + 0.12 * n3) * rim;
    return [r * v, g * v, b * v];
  };
  // +zone: big maps stream (docs/design/zones.md "Scale"): terrain colours are painted lazily, per
  // vertex, the first time a terrain cell or a grass tuft needs them.
  const terrainColors = new Float32Array(G.nx * G.ny * 3);
  const painted = new Uint8Array(G.nx * G.ny);
  const paintVertex = (k: number): void => {
    if (painted[k]) return;
    painted[k] = 1;
    const i = k % G.nx;
    const c = groundColor(k, G.x0 + i * G.cell, G.y0 + ((k - i) / G.nx) * G.cell);
    terrainColors[k * 3] = c[0];
    terrainColors[k * 3 + 1] = c[1];
    terrainColors[k * 3 + 2] = c[2];
  };
  const buildTerrain = (i0: number, j0: number, i1: number, j1: number): THREE.Mesh => {
    for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) paintVertex(j * G.nx + i);
    const geo = gridGeometry(L, i0, j0, i1, j1, terrainColors, GRASS_TILE);
    const m = new THREE.Mesh(geo, groundMat);
    m.name = 'terrain';
    m.receiveShadow = true;
    m.userData['noStyle'] = true;
    group.add(m);
    return m;
  };

  // ---- town cobbles -------------------------------------------------------------------------
  // +zone: a zone's town square has cobbles too (plaza weights from its safe town area).
  const cobble = theme.ground.cobble ?? (L.plaza.some((v) => v > 0) ? 0xb4ab9a : undefined);
  if (cobble !== undefined) {
    const cobTex = makeCobbleTexture(maxAniso);
    if (cobTex) owned.push(cobTex);
    const cobMat = ownedToonMaterial({ color: cobble, map: cobTex, vertexColors: true, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -2 });
    cobMat.name = 'field-cobble';
    owned.push(cobMat);
    const geo = overlayGeometry(L, L.plaza, COBBLE_TILE);
    if (geo) {
      owned.push(geo);
      const m = new THREE.Mesh(geo, cobMat);
      m.renderOrder = 1;
      m.receiveShadow = true;
      m.userData['noStyle'] = true;
      group.add(m);
      drawables++;
    }
  }

  // ---- props --------------------------------------------------------------------------------
  const atlas = assets.propAtlas();
  const propMat = occludable(ownedToonMaterial({ color: 0xffffff, map: atlas, vertexColors: true })); // +polish: fade between camera and hero
  let propHullMat: THREE.Material | null = null;
  propMat.name = 'field-props';
  owned.push(propMat);
  const procCache = new Map<string, THREE.BufferGeometry | null>();
  owned.push({ dispose: () => procCache.forEach((g) => g?.dispose()) });
  const source = (id: string): THREE.BufferGeometry | null => {
    if (/^k[dhn]_/.test(id)) return assets.kitGeometry(id);
    const had = procCache.has(id);
    const g = outdoorProc(id, procCache);
    if (g && !had) {
      const n = (g.attributes['position'] as THREE.BufferAttribute).count;
      const uv = new Float32Array(n * 2);
      for (let i = 0; i < n; i++) {
        uv[i * 2] = ATLAS_WHITE[0];
        uv[i * 2 + 1] = ATLAS_WHITE[1];
      }
      g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
      addOutlineNormals(g);
    }
    return g;
  };
  // +scenery: trees / rocks / cliffs: no hull, wind sway on the trees (aSway = yd above the trunk base).
  let natureMatV: THREE.MeshToonMaterial | null = null;
  const natureMat = (): THREE.MeshToonMaterial => {
    if (natureMatV) return natureMatV;
    const m = ownedToonMaterial({ color: 0xffffff, map: atlas, vertexColors: true });
    m.name = 'field-nature';
    const base = m.onBeforeCompile;
    m.onBeforeCompile = (sh, r) => {
      base.call(m, sh, r);
      sh.uniforms['uWindT'] = U.uTime;
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nattribute float aSway; uniform float uWindT; varying float vCanopy;')
        .replace('#include <begin_vertex>', `#include <begin_vertex>\n  vCanopy = aSway;\n${TREE_SWAY_GLSL}`);
      // Canopy light: crowns brighten and warm toward the sky-lit top (painted foliage, not flat cones).
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\nvarying float vCanopy;')
        .replace('#include <color_fragment>', '#include <color_fragment>\n  { float tk = clamp(vCanopy / 11.0, 0.0, 1.0); diffuseColor.rgb *= mix(vec3(0.9, 0.93, 0.97), vec3(1.16, 1.18, 1.06), tk) * step(0.001, vCanopy) + (1.0 - step(0.001, vCanopy)); }');
    };
    m.customProgramCacheKey = () => 'toon-nature';
    natureMatV = occludable(m);
    owned.push(natureMatV);
    return natureMatV;
  };
  // +valley: unlit crystals (lighting baked into the vertex colours; they glow through the bloom).
  let glowMat: THREE.MeshBasicMaterial | null = null;
  let groundPropMat: THREE.MeshToonMaterial | null = null;
  let glowHullMat: THREE.Material | null = null;
  const occludingHull = (hull: THREE.Mesh): THREE.Material => {
    const src = hull.material as THREE.Material;
    const clone = src.clone();
    clone.onBeforeCompile = src.onBeforeCompile; // clone() drops the outline hook
    owned.push(clone);
    return occludable(clone);
  };
  // +zone: distance culling (draw distance per kind; fog hides the cut). Re-evaluated a few times a second.
  type CullEntry = { o: THREE.Object3D; x: number; z: number; far: number; detail: boolean };
  const culled = new Set<CullEntry>();
  const cull = (o: THREE.Object3D, geo: THREE.BufferGeometry, far: number, isDetail: boolean): CullEntry => {
    geo.computeBoundingSphere();
    const c = geo.boundingSphere!.center;
    const e = { o, x: c.x, z: c.z, far: far + geo.boundingSphere!.radius, detail: isDetail };
    culled.add(e);
    return e;
  };
  const detailSet = new Set<THREE.Object3D>();
  let detailOn = true;
  let cullTick = 0;
  const chunks = new Map<string, OutPlacement[]>();
  const landmarks: OutPlacement[] = []; // +detail
  for (const p of L.placements) {
    // +detail: landmarks (the Hollowcrown) are built once, always drawn (seen across the map).
    if (LANDMARK_ID.test(p.id)) {
      landmarks.push(p);
      continue;
    }
    // +scenery: natural props (trees, rocks, cliffs, shrubs) merge apart from built ones: no outline
    // hull on terrain and foliage (painted shading reads cleaner), and they sway in the wind.
    const lay = p.layer === 'prop' && NATURE_ID.test(p.id) ? 'nature' : p.layer;
    const cs = lay === 'nature' ? NATURE_CHUNK : CHUNK; // +detail: nature merges per 64 yd (fewer draws)
    const key = `${Math.floor(p.pos.x / cs)}|${Math.floor(p.pos.y / cs)}|${lay}`;
    const l = chunks.get(key);
    if (l) l.push(p);
    else chunks.set(key, [p]);
  }
  const propCount = L.placements.length;
  /** One streamed piece: a mesh (with its hull, its cull entry) and the geometry it owns. */
  type Piece = { o: THREE.Object3D; geo: THREE.BufferGeometry | null; hull: THREE.Object3D | null; entry: CullEntry | null };
  const buildPropChunk = (key: string, list: OutPlacement[]): Piece | null => {
    const isDetail = key.endsWith('detail');
    const geo = mergePlacements(list, source, key.endsWith('nature') ? SWAY_ID : null);
    if (!geo) return null;
    if (key.endsWith('glow')) {
      if (!glowMat) {
        glowMat = occludable(new THREE.MeshBasicMaterial({ color: 0xffffff, vertexColors: true }));
        glowMat.name = 'field-glow';
        owned.push(glowMat);
      }
      const gmesh = new THREE.Mesh(geo, glowMat);
      gmesh.name = `glow:${key}`;
      gmesh.userData['noStyle'] = true;
      group.add(gmesh);
      const entry = cull(gmesh, geo, DRAW.glow, false);
      const hull = addHull(gmesh, glowMat, hulls, 'prop');
      hull.material = glowHullMat ??= occludingHull(hull);
      return { o: gmesh, geo, hull, entry };
    }
    if (key.endsWith('nature')) {
      const nmesh = new THREE.Mesh(geo, natureMat());
      nmesh.name = `nature:${key}`;
      nmesh.castShadow = true;
      nmesh.receiveShadow = true;
      nmesh.userData['noStyle'] = true;
      group.add(nmesh);
      const entry = cull(nmesh, geo, DRAW.prop, false);
      return { o: nmesh, geo, hull: null, entry };
    }
    const isGround = key.endsWith('ground');
    // +zone: flat ground stones never fade (a hole in the path at the camera's feet reads as a bug).
    if (isGround && !groundPropMat) {
      groundPropMat = ownedToonMaterial({ color: 0xffffff, map: atlas, vertexColors: true });
      groundPropMat.name = 'field-ground-props';
      owned.push(groundPropMat);
    }
    const mesh = new THREE.Mesh(geo, isGround ? groundPropMat! : propMat);
    mesh.name = `props:${key}`;
    mesh.castShadow = !isDetail && !isGround;
    mesh.receiveShadow = true;
    mesh.userData['noStyle'] = true;
    group.add(mesh);
    const entry = cull(mesh, geo, isDetail ? DRAW.detail : isGround ? DRAW.ground : DRAW.prop, isDetail);
    let hull: THREE.Object3D | null = null;
    // +scenery: flagstones have no outline hull either (painted paths, like the terrain).
    if (isGround) hull = null;
    else if (!isDetail) {
      const h = addHull(mesh, propMat, hulls, 'prop');
      // +polish: the (shared, cached) outline material gets a private occluding clone.
      if (!propHullMat) {
        const src = h.material as THREE.Material;
        const clone = src.clone();
        clone.onBeforeCompile = src.onBeforeCompile; // clone() drops the outline hook
        propHullMat = occludable(clone);
        owned.push(propHullMat);
      }
      h.material = propHullMat;
      hull = h;
    } else {
      detailSet.add(mesh);
      mesh.visible = detailOn;
    }
    return { o: mesh, geo, hull, entry };
  };
  const dropPiece = (p: Piece): void => {
    p.o.removeFromParent();
    if (p.hull) {
      p.hull.removeFromParent();
      hulls.prop.delete(p.hull);
    }
    if (p.entry) culled.delete(p.entry);
    detailSet.delete(p.o);
    if ((p.o as THREE.InstancedMesh).isInstancedMesh) (p.o as THREE.InstancedMesh).dispose();
    p.geo?.dispose();
  };

  // ---- far trees (instanced, chunked) ---------------------------------------------------------
  const farMat = ownedToonMaterial({ color: 0xffffff, vertexColors: true });
  farMat.name = 'field-far-trees';
  owned.push(farMat);
  const farRound = theme.farTree.roundLeaf !== undefined ? farBroadleafGeometry(theme.farTree.roundLeaf) : farTreeGeometry(false, theme.farTree.leaf); // +detail: cheap broadleaf far trees
  const farPine = theme.farTree.kind === 'spruce' ? farSpruceGeometry(theme.farTree.leaf) : farTreeGeometry(true, theme.farTree.leaf);
  addOutlineNormals(farPine);
  owned.push(farRound, farPine);
  const farChunks = new Map<string, typeof L.far>();
  for (const f of L.far) {
    const key = `${Math.floor(f.pos.x / 128)}|${Math.floor(f.pos.y / 128)}|${f.pine ? 1 : 0}`; // +atmos: 128 yd chunks, drawn to FAR_TREE_DRAW (far LOD stand-ins beyond)
    const l = farChunks.get(key);
    if (l) l.push(f);
    else farChunks.set(key, [f]);
  }
  {
    const M4 = new THREE.Matrix4();
    const Q = new THREE.Quaternion();
    const V = new THREE.Vector3();
    const S = new THREE.Vector3();
    const C = new THREE.Color();
    const AY = new THREE.Vector3(0, 1, 0);
    for (const [key, list] of farChunks) {
      const pine = key.endsWith('|1');
      const im = new THREE.InstancedMesh(pine ? farPine : farRound, farMat, list.length);
      list.forEach((f, i) => {
        M4.compose(V.set(f.pos.x, heightAt(f.pos.x, f.pos.y) - 0.1, -f.pos.y), Q.setFromAxisAngle(AY, f.yaw), S.setScalar(f.scale));
        im.setMatrixAt(i, M4);
        const v = 0.86 + 0.24 * vnoise(f.pos.x * 0.07, f.pos.y * 0.07);
        const ft = f.tint ?? [1, 1, 1];
        im.setColorAt(i, C.setRGB(v * ft[0], v * (0.97 + 0.06 * vnoise(f.pos.x * 0.2, 3)) * ft[1], v * 0.95 * ft[2]));
      });
      im.computeBoundingSphere();
      im.castShadow = true;
      im.receiveShadow = true;
      im.userData['noStyle'] = true;
      im.name = 'far-trees';
      group.add(im);
      // +atmos: the detailed far forest stops at FAR_TREE_DRAW; atmosphere/farLod.ts stand-ins carry it to the horizon.
      culled.add({ o: im, x: im.boundingSphere!.center.x, z: im.boundingSphere!.center.z, far: FAR_TREE_DRAW + im.boundingSphere!.radius, detail: false });
      drawables++; // +scenery: no outline hull on the far forest
    }
  }

  // ---- grass tufts --------------------------------------------------------------------------
  const groundAt = (x: number, y: number): [number, number, number] => {
    const i = Math.min(G.nx - 1, Math.max(0, Math.round((x - G.x0) / G.cell)));
    const j = Math.min(G.ny - 1, Math.max(0, Math.round((y - G.y0) / G.cell)));
    const k = j * G.nx + i;
    paintVertex(k);
    return [terrainColors[k * 3]!, terrainColors[k * 3 + 1]!, terrainColors[k * 3 + 2]!];
  };
  const grass = makeGrass(L, theme, heightAt, theme.grass.fromGround ? groundAt : null);
  owned.push(grass);

  // ---- +zone: streaming: 64 yd cells build (terrain, props, grass) around the camera ----------
  const SC = STREAM_CELL;
  const gx1 = G.x0 + (G.nx - 1) * G.cell;
  const gy1 = G.y0 + (G.ny - 1) * G.cell;
  const vi = (x: number): number => Math.min(G.nx - 1, Math.max(0, Math.round((x - G.x0) / G.cell)));
  const vj = (y: number): number => Math.min(G.ny - 1, Math.max(0, Math.round((y - G.y0) / G.cell)));
  type Cell = { ix: number; iy: number; cx: number; cy: number; chunkKeys: string[]; detailKeys: string[]; pieces: Piece[] | null; grass: Piece[] | null; d: number }; // +detail: detailKeys
  const cells: Cell[] = [];
  const cellIndex = new Map<string, Cell>();
  for (let iy = Math.floor(G.y0 / SC); iy * SC < gy1; iy++)
    for (let ix = Math.floor(G.x0 / SC); ix * SC < gx1; ix++) {
      const c: Cell = { ix, iy, cx: (ix + 0.5) * SC, cy: (iy + 0.5) * SC, chunkKeys: [], detailKeys: [], pieces: null, grass: null, d: Infinity };
      cells.push(c);
      cellIndex.set(`${ix}|${iy}`, c);
    }
  for (const key of chunks.keys()) {
    const [cx, cy] = key.split('|').map(Number) as [number, number];
    // +detail: walk-through detail streams with the grass (built within ~90 yd, not the props' ~230): less GPU memory.
    const cs = key.endsWith('nature') ? NATURE_CHUNK : CHUNK;
    const cell = cellIndex.get(`${Math.floor((cx * cs) / SC)}|${Math.floor((cy * cs) / SC)}`);
    if (key.endsWith('detail')) cell?.detailKeys.push(key);
    else cell?.chunkKeys.push(key);
  }
  /**
   * +zone: streamed geometry keeps no CPU copy once it is on the GPU (the JS heap would otherwise
   * hold every built cell twice); bounds are computed before the first draw.
   */
  const freeAfterUpload = (geo: THREE.BufferGeometry): void => {
    geo.computeBoundingSphere();
    geo.computeBoundingBox();
    const drop = function (this: THREE.BufferAttribute): void {
      (this as unknown as { array: ArrayLike<number> }).array = EMPTY_ARRAY;
    };
    for (const k of Object.keys(geo.attributes)) (geo.attributes[k] as THREE.BufferAttribute).onUpload(drop);
    geo.index?.onUpload(drop);
  };
  const buildCell = (c: Cell): void => {
    const t = now();
    const pieces: Piece[] = [];
    const i0 = vi(c.ix * SC);
    const i1 = vi((c.ix + 1) * SC);
    const j0 = vj(c.iy * SC);
    const j1 = vj((c.iy + 1) * SC);
    if (i1 > i0 && j1 > j0) {
      const m = buildTerrain(i0, j0, i1, j1);
      pieces.push({ o: m, geo: m.geometry, hull: null, entry: null });
    }
    for (const key of c.chunkKeys) {
      const p = buildPropChunk(key, chunks.get(key)!);
      if (p) pieces.push(p);
    }
    for (const p of pieces) if (p.geo) freeAfterUpload(p.geo);
    c.pieces = pieces;
    setMask(c, true);
    noteBuild(now() - t);
  };
  const buildCellGrass = (c: Cell): void => {
    const t = now();
    c.grass = grass.region(c.ix * SC, c.iy * SC, SC, (c.ix * 7919 + c.iy * 104729) >>> 0).map((im) => {
      group.add(im);
      detailSet.add(im);
      im.visible = detailOn;
      const bs = im.boundingSphere!;
      const entry: CullEntry = { o: im, x: bs.center.x, z: bs.center.z, far: DRAW.grass + bs.radius, detail: true };
      culled.add(entry);
      return { o: im, geo: null, hull: null, entry };
    });
    // +detail: the cell's walk-through detail chunks come and go with its grass.
    for (const key of c.detailKeys) {
      const p = buildPropChunk(key, chunks.get(key)!);
      if (!p) continue;
      if (p.geo) freeAfterUpload(p.geo);
      c.grass.push(p);
    }
    noteBuild(now() - t);
  };
  const streamStats = { cells: cells.length, built: 0, grass: 0, lastMs: 0, maxMs: 0, totalMs: 0, builds: 0 };
  const noteBuild = (ms: number): void => {
    streamStats.lastMs = ms;
    streamStats.maxMs = Math.max(streamStats.maxMs, ms);
    streamStats.totalMs += ms;
    streamStats.builds++;
  };
  (globalThis as { __fieldStream?: typeof streamStats }).__fieldStream = streamStats;

  // +zone: the far terrain: one coarse mesh of the whole map under the streamed cells (a texture mask
  // hides it where a detailed cell is built), so the horizon never ends in a void.
  const minIx = Math.min(...cells.map((c) => c.ix));
  const minIy = Math.min(...cells.map((c) => c.iy));
  const maskW = Math.max(...cells.map((c) => c.ix)) - minIx + 1;
  const maskH = Math.max(...cells.map((c) => c.iy)) - minIy + 1;
  const maskData = new Uint8Array(maskW * maskH);
  const maskTex = new THREE.DataTexture(maskData, maskW, maskH, THREE.RedFormat, THREE.UnsignedByteType);
  maskTex.magFilter = THREE.NearestFilter;
  maskTex.minFilter = THREE.NearestFilter;
  maskTex.needsUpdate = true;
  owned.push(maskTex);
  const setMask = (c: Cell, on: boolean): void => {
    maskData[(c.iy - minIy) * maskW + (c.ix - minIx)] = on ? 255 : 0;
    maskTex.needsUpdate = true;
  };
  const farGroundMat = ownedToonMaterial({ color: 0xffffff, vertexColors: true });
  farGroundMat.name = 'field-far-ground';
  {
    const base = farGroundMat.onBeforeCompile;
    const uMask = { value: maskTex };
    const uMaskBox = { value: new THREE.Vector4(minIx * SC, minIy * SC, maskW * SC, maskH * SC) };
    farGroundMat.onBeforeCompile = (sh, r) => {
      base.call(farGroundMat, sh, r);
      sh.uniforms['uMask'] = uMask;
      sh.uniforms['uMaskBox'] = uMaskBox;
      sh.vertexShader = sh.vertexShader.replace('#include <common>', '#include <common>\nvarying vec2 vSimXY;').replace('#include <project_vertex>', '#include <project_vertex>\n  { vec4 wq = modelMatrix * vec4(transformed, 1.0); vSimXY = vec2(wq.x, -wq.z); }');
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\nvarying vec2 vSimXY; uniform sampler2D uMask; uniform vec4 uMaskBox;')
        .replace('void main() {', 'void main() {\n  { vec2 mq = (vSimXY - uMaskBox.xy) / uMaskBox.zw; if (texture2D(uMask, mq).r > 0.5) discard; }');
    };
    farGroundMat.customProgramCacheKey = () => 'toon-far-ground';
  }
  owned.push(farGroundMat);
  const farGround = (() => {
    const stride = Math.max(1, Math.round(FAR_GROUND_STEP / G.cell));
    const geo = gridGeometry(L, 0, 0, G.nx - 1, G.ny - 1, terrainColors, GRASS_TILE, stride, (k) => paintVertex(k));
    const m = new THREE.Mesh(geo, farGroundMat);
    m.name = 'terrain-far';
    m.position.y = -0.08;
    m.receiveShadow = false;
    m.userData['noStyle'] = true;
    group.add(m);
    owned.push(geo);
    drawables++;
    return m;
  })();
  void farGround;
  let queue: { c: Cell; grass: boolean }[] = [];
  let lastStreamAt: { x: number; y: number } | null = null;
  /** Re-plan: which cells to build / drop around (x, y) in sim yards; nearest first. */
  const plan = (x: number, y: number): void => {
    queue = [];
    for (const c of cells) {
      // Distance from the point to the cell's square (0 inside it).
      const dx = Math.max(0, Math.abs(x - c.cx) - SC / 2);
      const dy = Math.max(0, Math.abs(y - c.cy) - SC / 2);
      c.d = Math.hypot(dx, dy);
      if (c.pieces && c.d > STREAM_DROP) {
        for (const p of c.pieces) dropPiece(p);
        c.pieces = null;
        setMask(c, false);
      } else if (!c.pieces && c.d < STREAM_BUILD) queue.push({ c, grass: false });
      if (c.grass && c.d > GRASS_DROP) {
        for (const p of c.grass) dropPiece(p);
        c.grass = null;
      } else if (!c.grass && c.d < GRASS_BUILD) queue.push({ c, grass: true });
    }
    queue.sort((a, b) => a.c.d - b.c.d || Number(a.grass) - Number(b.grass));
    streamStats.built = cells.filter((c) => c.pieces).length;
    streamStats.grass = cells.filter((c) => c.grass).length;
  };
  /** Build queued cells: everything near (x, y) at once (arrival, a waystone jump), then a few ms a frame. */
  const pump = (budgetMs: number, mustWithin: number): void => {
    const t0 = now();
    while (queue.length) {
      const q = queue[0]!;
      if (q.c.d > mustWithin && now() - t0 > budgetMs) break;
      queue.shift();
      if (q.grass) {
        if (!q.c.grass) buildCellGrass(q.c);
      } else if (!q.c.pieces) buildCell(q.c);
    }
  };
  // Debug / capture hooks: finish the queue now; build every cell (a top-down overview).
  (globalThis as { __fieldStreamFlush?: () => void }).__fieldStreamFlush = () => pump(1e9, 1e9);
  (globalThis as { __fieldBuildAll?: () => void }).__fieldBuildAll = () => {
    for (const c of cells) if (!c.pieces) buildCell(c);
  };
  (globalThis as { __toonU?: typeof U }).__toonU = U; // capture: lift the haze for top-down overviews
  const stream = (x: number, y: number): void => {
    const jumped = !lastStreamAt || Math.hypot(x - lastStreamAt.x, y - lastStreamAt.y) > 60;
    if (jumped || cullTick <= 0) {
      plan(x, y);
      lastStreamAt = { x, y };
    }
    pump(STREAM_BUDGET_MS, jumped ? STREAM_NOW : 0);
  };

  // ---- lights: flames + glow cards ---------------------------------------------------------
  const glows: GlowSpec[] = [];
  const flames: { x: number; y: number; z: number; w: number; h: number }[] = [];
  for (const l of L.lights) {
    const gy = heightAt(l.pos.x, l.pos.y);
    const x = l.pos.x;
    const z = -l.pos.y;
    if (l.kind === 'lantern') {
      flames.push({ x, y: gy + l.y - 0.18, z, w: 0.22, h: 0.36 });
      glows.push({ x, y: gy + l.y, z, size: 1.6, color: [0.55, 0.36, 0.14], pulse: 0.12 });
    } else if (l.kind === 'fire') {
      flames.push({ x, y: gy + 0.1, z, w: 1.1, h: 1.7 });
      glows.push({ x, y: gy + 0.7, z, size: 4, color: [0.5, 0.26, 0.08], pulse: 0.2 });
    } else if (l.kind === 'forge') {
      flames.push({ x, y: gy + l.y - 0.1, z, w: 0.8, h: 0.7 });
      glows.push({ x, y: gy + l.y + 0.2, z, size: 2.6, color: [0.6, 0.3, 0.08], pulse: 0.25 });
    } else if (l.kind === 'monolith') {
      // +valley: the crystal monument's halo (big, soft, cool) and a tighter core.
      const c = theme.crystalGlow ?? [0.18, 0.62, 0.6];
      glows.push({ x, y: gy + l.y, z, size: 16, color: [c[0] * 0.6, c[1] * 0.6, c[2] * 0.6], pulse: 0.25 });
      glows.push({ x, y: gy + l.y + 1.0, z, size: 6.5, color: [c[0] * 0.9 + 0.15, c[1] * 0.9 + 0.2, c[2] * 0.9], pulse: 0.4 });
    } else {
      glows.push({ x, y: gy + l.y, z, size: 2.8, color: theme.crystalGlow ?? [0.18, 0.62, 0.6], pulse: 0.45 });
    }
  }
  if (flames.length) {
    const fGeo = new THREE.PlaneGeometry(1, 1).translate(0, 0.5, 0);
    const fMat = flameMaterial();
    owned.push(fGeo, fMat);
    const fm = new THREE.InstancedMesh(fGeo, fMat, flames.length);
    const Q0 = new THREE.Quaternion();
    flames.forEach((f, i) => fm.setMatrixAt(i, new THREE.Matrix4().compose(new THREE.Vector3(f.x, f.y, f.z), Q0, new THREE.Vector3(f.w, f.h, 1))));
    fm.frustumCulled = false;
    group.add(fm);
    drawables++;
  }
  const gm = glowCards(glows);
  if (gm) {
    owned.push(gm.geometry, gm.material as THREE.Material);
    group.add(gm);
    drawables++;
  }

  // Fireflies / pollen drifting over the walkable ground (meadow + forest).
  if (theme.id !== 'town') {
    const bbW = d.bounds.w;
    const bbH = d.bounds.h;
    const ff = motes(theme.id === 'forest' ? 70 : 50, Math.max(bbW, bbH) * 0.5, 3.5, theme.id === 'forest' ? [0.75, 1.0, 0.45] : [1.0, 0.95, 0.7], 0.12, 1, 0.6);
    ff.position.set(d.bounds.x + bbW / 2, 0, -(d.bounds.y + bbH / 2));
    owned.push(ff.geometry, ff.material as THREE.Material);
    group.add(ff);
    detail.push(ff);
    drawables++;
  }

  // ---- +atmos: sky, clouds, the distant ranges (real geometry), far LOD stand-ins, light shafts ----
  const towerHint = (d.props ?? []).find((h) => h.kind === 'tower_landmark');
  const towerDir = towerHint ? { x: towerHint.pos.x - (d.bounds.x + d.bounds.w / 2), y: towerHint.pos.y - (d.bounds.y + d.bounds.h / 2) } : null;
  const atmos = buildAtmosphere({
    day: { sky: theme.sky, lighting: theme.lighting },
    cloudCover: theme.sky.clouds?.cover ?? 0.5,
    extent: { x0: G.x0, y0: G.y0, x1: gx1, y1: gy1 },
    heightAt,
    towerDir,
    placements: L.placements,
    farTrees: L.far.map((f) => ({ x: f.pos.x, y: f.pos.y, base: heightAt(f.pos.x, f.pos.y) - 0.1, scale: f.scale, pine: f.pine, yaw: f.yaw, tint: f.tint ?? null })),
    leaf: theme.farTree.leaf,
    shafts: d.id === 'mossvale' ? MOSSVALE_SHAFT_SPOTS.map((p) => ({ ...p, h: heightAt(p.x, p.y) })) : [],
  });
  group.add(atmos.group);
  drawables += 3;

  // ---- the Tower: stands among the distant ranges in its direction (outdoor/tower.ts) ----------
  // +atmos: world-anchored (real parallax) at its hint, or just inside the ranges if the hint is further.
  const towerPos = towerHint && towerDir ? (() => {
    const l = Math.hypot(towerDir.x, towerDir.y) || 1;
    const cx = d.bounds.x + d.bounds.w / 2;
    const cy = d.bounds.y + d.bounds.h / 2;
    const r = Math.min(l, Math.max(d.bounds.w, d.bounds.h) / 2 + RANGE_REACH * 0.35);
    return { x: cx + (towerDir.x / l) * r, y: cy + (towerDir.y / l) * r };
  })() : null;
  const tower = towerDir && towerPos ? buildTowerLandmark(towerDir, theme.sky.horizon, theme.towerStyle, { x: towerPos.x, y: atmos.towerAt(towerPos.x, towerPos.y) - 6, z: -towerPos.y, scale: 2.4 }) : null;
  if (tower) {
    group.add(tower.group);
    atmos.tintLand(tower.group); // +atmos
    drawables += 4;
  }

  // ---- +zone: water (the lake, the river, the falls) ----------------------------------------------
  const terr = fieldTerrainOf(d.id);
  const water = terr?.water ? buildWater(terr.water, terr.heightAt, G.cell, atmos.waterSky /* +atmos: time-of-day sky colours, shared in place */) : null;
  if (water) {
    group.add(water.group);
    drawables += 4;
  }

  // ---- +detail: landmarks (one always-drawn nature mesh) and ambient life ----------------------------
  if (landmarks.length) {
    const lgeo = mergePlacements(landmarks, source, /^$/);
    if (lgeo) {
      const lm = new THREE.Mesh(lgeo, natureMat());
      lm.name = 'landmarks';
      lm.castShadow = true;
      lm.receiveShadow = true;
      lm.userData['noStyle'] = true;
      group.add(lm);
      owned.push(lgeo);
      drawables++;
    }
  }
  const life = L.terrain ? buildLife(L, heightAt, terr?.water ?? null) : null;
  if (life) {
    group.add(life.group);
    drawables += 8;
  }

  // ---- portals --------------------------------------------------------------------------------
  const portals = buildPortals(d.field?.portals ?? [], heightAt);
  group.add(portals.group);
  owned.push(portals);

  // ---- camera blockers: the map's edge (the edge band is trees / houses / hills the camera must
  // not sink into: from inside a hull the screen fills with outline colour) and cottage walls ----
  const blockers: Blocker[] = blockersFromWalkable(d.rooms.map((r) => r.shape), 40);
  for (const p of L.placements) {
    if (!p.id.startsWith('cottage')) continue;
    const hw = 3.1 * p.scale;
    const hd = 2.4 * p.scale;
    const c = Math.cos(p.yaw);
    const s = Math.sin(p.yaw);
    // three: x = lx·c + lz·s, z = -lx·s + lz·c; sim y = -z.
    const corner = (lx: number, lz: number) => ({ x: p.pos.x + lx * c + lz * s, y: p.pos.y + lx * s - lz * c });
    const q = [corner(-hw, -hd), corner(hw, -hd), corner(hw, hd), corner(-hw, hd)];
    const top = p.y + 3.4;
    for (let i = 0; i < 4; i++) blockers.push({ a: q[i]!, b: q[(i + 1) % 4]!, top });
  }

  const ms = typeof performance !== 'undefined' ? performance.now() - t0 : 0;
  if (typeof console !== 'undefined' && ms > 0) console.info(`[toon] field ${d.id} (${theme.id}) built in ${ms.toFixed(0)} ms: ${propCount} props, ${L.far.length} far trees, streamed in ${cells.length} cells of ${STREAM_CELL} yd, ${drawables} fixed draws`);

  const dtheme = outdoorDungeonTheme(theme);
  return {
    group,
    floorMaterial: groundMat,
    theme: dtheme,
    arch: null,
    layout: L,
    hums: portals.hums,
    cameraBlockers: () => blockers,
    cutaway(cam: THREE.Vector3, focus: { x: number; y: number }, groundY: number) {
      updateFieldOcclusion(cam, focus.x, groundY + 1.1, -focus.y); // +polish
    },
    counts: { walls: 0, props: propCount, torches: flames.length, drawables, doors: 0, clusters: chunks.size },
    setFloorBrightness(v: number) {
      groundMat.color.setScalar(v);
    },
    setDetail(on: boolean) {
      detailOn = on;
      for (const o of detailSet) o.visible = on;
      for (const o of detail) o.visible = on;
      life?.setVisible(on); // +detail
      cullTick = 0;
    },
    ingest() {},
    update() {},
    resetDoors() {},
    frame(camera: THREE.Camera, hero: { x: number; y: number } | null) {
      stream(camera.position.x, -camera.position.z); // +zone
      if (cullTick-- <= 0) {
        cullTick = 8;
        const cx = camera.position.x;
        const cz = camera.position.z;
        for (const c of culled) {
          const want = (!c.detail || detailOn) && (c.x - cx) * (c.x - cx) + (c.z - cz) * (c.z - cz) < c.far * c.far;
          if (c.o.visible !== want) c.o.visible = want;
        }
      }
      atmos.frame(camera); // +atmos
      life?.frame(camera, hero); // +detail
      tower?.frame(camera, (typeof performance !== 'undefined' ? performance.now() : 0) / 1000);
      portals.update(hero);
    },
    dispose() {
      group.traverse((o) => {
        if (o.userData['hull']) {
          hulls.prop.delete(o);
          hulls.unit.delete(o);
        }
        if ((o as THREE.InstancedMesh).isInstancedMesh) (o as THREE.InstancedMesh).dispose();
      });
      for (const c of cells) {
        for (const p of c.pieces ?? []) dropPiece(p);
        for (const p of c.grass ?? []) dropPiece(p);
        c.pieces = c.grass = null;
      }
      tower?.dispose();
      life?.dispose(); // +detail
      atmos.dispose(); // +atmos
      water?.dispose();
      OCC_U.uOccP.value.z = 0;
      group.removeFromParent();
      for (const o of owned) o.dispose();
      setGroundFn(null);
    },
  };
}

/** +zone: cliff-face rock (linear RGB). */
const ROCK = lin(0x7c8698);

const smooth01 = (e0: number, e1: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

/** Terrain chunk [i0..i1] × [j0..j1] of the layout grid (indexed, smooth normals from heights). */
function gridGeometry(L: FieldLayout, i0: number, j0: number, i1: number, j1: number, colors: Float32Array, tile: number, stride = 1, prepare?: (k: number) => void): THREE.BufferGeometry {
  const G = L.grid;
  // +zone: `stride` > 1 samples every stride-th vertex (the coarse far terrain).
  const w = Math.floor((i1 - i0) / stride) + 1;
  const h = Math.floor((j1 - j0) / stride) + 1;
  const pos = new Float32Array(w * h * 3);
  const nrm = new Float32Array(w * h * 3);
  const uv = new Float32Array(w * h * 2);
  const col = new Float32Array(w * h * 3);
  const H = (i: number, j: number): number => {
    const ii = Math.min(G.nx - 1, Math.max(0, i));
    const jj = Math.min(G.ny - 1, Math.max(0, j));
    return L.height[jj * G.nx + ii]!;
  };
  for (let j = 0; j < h; j++)
    for (let i = 0; i < w; i++) {
      const gi = i0 + i * stride;
      const gj = j0 + j * stride;
      const k = gj * G.nx + gi;
      prepare?.(k);
      const o = j * w + i;
      const x = G.x0 + gi * G.cell;
      const y = G.y0 + gj * G.cell;
      pos[o * 3] = x;
      pos[o * 3 + 1] = L.height[k]!;
      pos[o * 3 + 2] = -y;
      // Normal from central differences (sim y → three -z).
      const dx = (H(gi + stride, gj) - H(gi - stride, gj)) / (2 * G.cell * stride);
      const dy = (H(gi, gj + stride) - H(gi, gj - stride)) / (2 * G.cell * stride);
      const nl = Math.hypot(dx, 1, dy);
      nrm[o * 3] = -dx / nl;
      nrm[o * 3 + 1] = 1 / nl;
      nrm[o * 3 + 2] = dy / nl;
      uv[o * 2] = x / tile;
      uv[o * 2 + 1] = y / tile;
      col[o * 3] = colors[k * 3]!;
      col[o * 3 + 1] = colors[k * 3 + 1]!;
      col[o * 3 + 2] = colors[k * 3 + 2]!;
    }
  const idx: number[] = [];
  for (let j = 0; j < h - 1; j++)
    for (let i = 0; i < w - 1; i++) {
      const a = j * w + i;
      const b = a + 1;
      const c = a + w;
      const e = c + 1;
      // sim +y is three -z: keep the winding facing up.
      idx.push(a, b, c, b, e, c);
    }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  g.setIndex(idx);
  g.computeBoundingSphere();
  return g;
}

/** Overlay (cobbles) over grid cells where `weight` > 0, vertex alpha = weight. */
function overlayGeometry(L: FieldLayout, weight: Float32Array, tile: number): THREE.BufferGeometry | null {
  const G = L.grid;
  const pos: number[] = [];
  const nrm: number[] = [];
  const uv: number[] = [];
  const col: number[] = [];
  const ids = new Map<number, number>();
  const idx: number[] = [];
  const vert = (k: number): number => {
    let v = ids.get(k);
    if (v !== undefined) return v;
    v = pos.length / 3;
    ids.set(k, v);
    const x = G.x0 + (k % G.nx) * G.cell;
    const y = G.y0 + Math.floor(k / G.nx) * G.cell;
    pos.push(x, L.height[k]! + 0.01, -y);
    nrm.push(0, 1, 0);
    uv.push(x / tile, y / tile);
    const w = weight[k]!;
    col.push(1, 1, 1, Math.min(1, w * 1.15));
    return v;
  };
  for (let j = 0; j < G.ny - 1; j++)
    for (let i = 0; i < G.nx - 1; i++) {
      const a = j * G.nx + i;
      const b = a + 1;
      const c = a + G.nx;
      const e = c + 1;
      if (weight[a]! + weight[b]! + weight[c]! + weight[e]! < 0.02) continue;
      idx.push(vert(a), vert(b), vert(c), vert(b), vert(e), vert(c));
    }
  if (!idx.length) return null;
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 4));
  g.setIndex(idx);
  g.computeBoundingSphere();
  return g;
}

/** Merge placements into one geometry (position, normal, outlineNormal, uv, color). */
function mergePlacements(list: readonly OutPlacement[], source: (id: string) => THREE.BufferGeometry | null, sway: RegExp | null = null): THREE.BufferGeometry | null {
  const srcs = list.map((p) => source(p.id));
  let total = 0;
  for (const g of srcs) total += g ? (g.attributes['position'] as THREE.BufferAttribute).count : 0;
  if (total === 0) return null;
  const pos = new Float32Array(total * 3);
  const nrm = new Float32Array(total * 3);
  const onr = new Float32Array(total * 3);
  const uvs = new Float32Array(total * 2);
  const col = new Float32Array(total * 3);
  const swy = sway ? new Float32Array(total) : null;
  const M4 = new THREE.Matrix4();
  const N3 = new THREE.Matrix3();
  const Q = new THREE.Quaternion();
  const V = new THREE.Vector3();
  const S = new THREE.Vector3();
  const AY = new THREE.Vector3(0, 1, 0);
  let o = 0;
  list.forEach((p, idx) => {
    const g = srcs[idx];
    if (!g) return;
    M4.compose(V.set(p.pos.x, p.y, -p.pos.y), Q.setFromAxisAngle(AY, p.yaw), S.setScalar(p.scale));
    N3.getNormalMatrix(M4);
    const P = (g.attributes['position'] as THREE.BufferAttribute).array as Float32Array;
    const Nn = (g.attributes['normal'] as THREE.BufferAttribute).array as Float32Array;
    const On = ((g.attributes['outlineNormal'] as THREE.BufferAttribute | undefined)?.array as Float32Array | undefined) ?? Nn;
    const Uv = (g.attributes['uv'] as THREE.BufferAttribute | undefined)?.array as Float32Array | undefined;
    const C = (g.attributes['color'] as THREE.BufferAttribute | undefined)?.array as Float32Array | undefined;
    const e = M4.elements;
    const ne = N3.elements;
    const count = P.length / 3;
    const [tr, tg, tb] = p.tint;
    const sw = swy && sway!.test(p.id) ? p.scale : 0;
    for (let i = 0; i < count; i++, o++) {
      if (swy) swy[o] = sw * Math.max(0, P[i * 3 + 1]!);
      const i3 = i * 3;
      const o3 = o * 3;
      const x = P[i3]!;
      const y = P[i3 + 1]!;
      const z = P[i3 + 2]!;
      pos[o3] = e[0]! * x + e[4]! * y + e[8]! * z + e[12]!;
      pos[o3 + 1] = e[1]! * x + e[5]! * y + e[9]! * z + e[13]!;
      pos[o3 + 2] = e[2]! * x + e[6]! * y + e[10]! * z + e[14]!;
      for (const [src, dst] of [
        [Nn, nrm],
        [On, onr],
      ] as const) {
        const a = src[i3]!;
        const b = src[i3 + 1]!;
        const c = src[i3 + 2]!;
        const nx = ne[0]! * a + ne[3]! * b + ne[6]! * c;
        const ny = ne[1]! * a + ne[4]! * b + ne[7]! * c;
        const nz = ne[2]! * a + ne[5]! * b + ne[8]! * c;
        const l = 1 / (Math.hypot(nx, ny, nz) || 1);
        dst[o3] = nx * l;
        dst[o3 + 1] = ny * l;
        dst[o3 + 2] = nz * l;
      }
      uvs[o * 2] = Uv ? Uv[i * 2]! : ATLAS_WHITE[0];
      uvs[o * 2 + 1] = Uv ? Uv[i * 2 + 1]! : ATLAS_WHITE[1];
      col[o3] = (C ? C[i3]! : 1) * tr;
      col[o3 + 1] = (C ? C[i3 + 1]! : 1) * tg;
      col[o3 + 2] = (C ? C[i3 + 2]! : 1) * tb;
    }
  });
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos.subarray(0, o * 3), 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(nrm.subarray(0, o * 3), 3));
  geo.setAttribute('outlineNormal', new THREE.BufferAttribute(onr.subarray(0, o * 3), 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(uvs.subarray(0, o * 2), 2));
  geo.setAttribute('color', new THREE.BufferAttribute(col.subarray(0, o * 3), 3));
  if (swy) geo.setAttribute('aSway', new THREE.BufferAttribute(swy.subarray(0, o), 1));
  geo.computeBoundingSphere();
  return geo;
}

/** +scenery: natural props (no outline hull, merged apart from built props). */
const NATURE_ID = /^(spruce|fir|tree_|pine|bush|shrub|fern|mossrock|rock|boulder|cliff_|bramble|stump|log|scree|spoil_heap|mushrooms|flowers|bluebells|violets|barley|reeds)/;
/** +detail: landmark props built as one always-drawn mesh (no streaming, no sway). */
const LANDMARK_ID = /^eldertree/;
/** +scenery: the trees among them (they sway). */
const SWAY_ID = /^(spruce|fir|tree_|pine|bush|shrub)/;
/**
 * +scenery: tree sway (merged world-space geometry; aSway = yd above the base). A slow travelling
 * gust leans every crown downwind, a faster wobble keeps them alive; tops move most.
 */
const TREE_SWAY_GLSL = `
  if (aSway > 0.0) {
    vec2 wq = transformed.xz;
    float ph = wq.x * 0.11 + wq.y * 0.07;
    float gust = 0.55 + 0.45 * sin(uWindT * 0.5 - wq.x * 0.021 - wq.y * 0.013);
    float k = aSway * aSway * 0.0014 * gust;
    transformed.x += (0.55 + 0.45 * sin(uWindT * 1.2 + ph)) * k;
    transformed.z += cos(uWindT * 0.95 + ph * 1.3) * 0.3 * k;
  }`;

/** Low-poly distant tree for the instanced far ring. */
function farTreeGeometry(pine: boolean, leaf: number): THREE.BufferGeometry {
  const leafC = new THREE.Color(leaf);
  const light = leafC.clone().multiplyScalar(1.25);
  let g: THREE.BufferGeometry;
  if (pine) {
    g = combine([
      paint(new THREE.CylinderGeometry(0.2, 0.3, 2.6, 5).translate(0, 1.3, 0), OUT.barkDark, [0.6, 1]),
      paint(new THREE.ConeGeometry(2.4, 4.4, 7).translate(0, 4.0, 0), [leafC.r, leafC.g, leafC.b], [0.6, 1.0]),
      paint(new THREE.ConeGeometry(1.7, 3.6, 7).translate(0, 6.4, 0), [light.r, light.g, light.b], [0.75, 1.05]),
    ]);
  } else {
    g = combine([
      paint(new THREE.CylinderGeometry(0.28, 0.4, 3, 5).translate(0, 1.5, 0), OUT.bark, [0.6, 1]),
      paint(xf(new THREE.SphereGeometry(2.4, 8, 6), [0, 4.6, 0], [0, 0, 0], [1, 0.82, 1]), [leafC.r, leafC.g, leafC.b], [0.6, 1.1], [2.6, 7]),
      paint(xf(new THREE.SphereGeometry(1.4, 7, 5), [0.7, 6.0, 0.3]), [light.r, light.g, light.b], [0.8, 1.1], [2.6, 7]),
    ]);
  }
  addOutlineNormals(g);
  g.computeBoundingSphere();
  return g;
}

// ---------------------------------------------------------------------------------------------
// Grass
// ---------------------------------------------------------------------------------------------

function tuftGeometry(height: number): THREE.BufferGeometry {
  const pos: number[] = [];
  const col: number[] = [];
  const n = 6;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI + (i % 2) * 0.4;
    const r = 0.12 + (i % 3) * 0.05;
    const bx = Math.cos(a * 2.3) * r;
    const bz = Math.sin(a * 2.3) * r;
    const w = 0.07;
    const h = height * (0.7 + ((i * 37) % 10) / 30);
    const lean = 0.18 + (i % 2) * 0.1;
    const dx = Math.cos(a) * w;
    const dz = Math.sin(a) * w;
    const tx = bx + Math.cos(a * 2.3) * lean;
    const tz = bz + Math.sin(a * 2.3) * lean;
    // One tapered blade (two triangles, base pair + tip).
    pos.push(bx - dx, 0, bz - dz, bx + dx, 0, bz + dz, tx, h, tz);
    // +scenery: the root is the ground's own colour (a carpet, not dark spiky clumps), tips lighter / warmer.
    col.push(0.8, 0.8, 0.8, 0.8, 0.8, 0.8, 1.22, 1.2, 1.06);
    pos.push(bx + dx, 0, bz + dz, bx - dx, 0, bz - dz, tx, h, tz);
    col.push(0.8, 0.8, 0.8, 0.8, 0.8, 0.8, 1.22, 1.2, 1.06);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  // Up-facing normals: tufts shade like the ground under them (soft, no per-blade facets).
  g.setAttribute('normal', new THREE.Float32BufferAttribute(new Array((pos.length / 3) * 3).fill(0).map((_, i) => (i % 3 === 1 ? 1 : 0)), 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.computeBoundingSphere();
  return g;
}

/**
 * +zone: grass tufts, built per streamed region (`region(x, y, size, seed)`: instanced meshes per
 * CHUNK inside it, seeded per region so a rebuilt region looks the same).
 */
function makeGrass(L: FieldLayout, theme: OutdoorTheme, heightAt: (x: number, y: number) => number, groundColorAt: ((x: number, y: number) => [number, number, number]) | null): { region(x: number, y: number, size: number, seed: number): THREE.InstancedMesh[]; count: number; dispose(): void } {
  const geo = tuftGeometry(theme.grass.height);
  const mat = ownedToonMaterial({ color: 0xffffff, vertexColors: true, side: THREE.DoubleSide });
  mat.name = 'field-grass';
  const base = mat.onBeforeCompile;
  mat.onBeforeCompile = (sh, r) => {
    base.call(mat, sh, r);
    sh.uniforms['uShock'] = GRASS_SHOCK;
    sh.uniforms['uWindT'] = U.uTime;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', `#include <common>\nuniform vec4 uShock; uniform float uWindT; varying float vSheen;\n${NOISE_GLSL}`)
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        {
          // +scenery: tufts shrink away toward the draw distance instead of popping.
          vec4 wb0 = modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
          transformed *= 1.0 - smoothstep(${(DRAW.grass - 20).toFixed(1)}, ${(DRAW.grass - 2).toFixed(1)}, distance(wb0.xyz, cameraPosition));
        }`,
      )
      .replace(
        '#include <project_vertex>',
        `#include <project_vertex>
        {
          vec4 wb = modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
          float hk = clamp(position.y / ${theme.grass.height.toFixed(2)}, 0.0, 1.0);
          hk *= hk;
          float gust = noise(wb.xz * 0.05 + vec2(uWindT * 0.35, uWindT * 0.12));
          // +scenery: travelling gust waves: bright bands roll across the field downwind (bend + sheen).
          vec2 wdir = vec2(0.83, 0.55);
          float wave = sin(dot(wb.xz, wdir) * 0.16 - uWindT * 1.7 + noise(wb.xz * 0.02) * 4.0);
          float crest = smoothstep(0.35, 1.0, wave) * (0.45 + 0.55 * noise(wb.xz * 0.03 - uWindT * 0.08));
          vSheen = crest * hk;
          vec3 off = vec3(sin(uWindT * 1.9 + wb.x * 0.35 + wb.z * 0.21) * 0.07 + gust * 0.16, 0.0, cos(uWindT * 1.4 + wb.z * 0.3) * 0.045 + gust * 0.08) * hk;
          off.xz += wdir * crest * 0.32 * hk;
          off.y -= crest * 0.08 * hk;
          if (uShock.w > 0.0) {
            vec2 dv = wb.xz - uShock.xy; float dl = length(dv) + 1e-3;
            float fq = (dl - uShock.z) / 1.6; float front = exp(-fq * fq);
            off.xz += (dv / dl) * front * uShock.w * 0.9 * hk;
            off.y -= front * uShock.w * 0.25 * hk;
          }
          mvPosition += viewMatrix * vec4(off, 0.0);
          gl_Position = projectionMatrix * mvPosition;
        }`,
      );
  };
  {
    const vb = mat.onBeforeCompile;
    mat.onBeforeCompile = (sh, r) => {
      vb.call(mat, sh, r);
      sh.fragmentShader = sh.fragmentShader.replace('#include <common>', '#include <common>\nvarying float vSheen;').replace('#include <color_fragment>', '#include <color_fragment>\n  diffuseColor.rgb *= 1.0 + vSheen * vec3(0.26, 0.24, 0.12);');
    };
  }
  mat.customProgramCacheKey = () => `toon-grass-${theme.grass.height.toFixed(2)}`;
  const G = L.grid;
  const per = theme.grass.perYd2;
  // Jittered grid sampling; tufts thin out on paths / cobbles and stop a little into the hills.
  const step = 1 / Math.sqrt(Math.max(0.01, per));
  const gx0 = G.x0;
  const gy0 = G.y0;
  const gx1 = G.x0 + (G.nx - 1) * G.cell;
  const gy1 = G.y0 + (G.ny - 1) * G.cell;
  const M4 = new THREE.Matrix4();
  const Q = new THREE.Quaternion();
  const V = new THREE.Vector3();
  const S = new THREE.Vector3();
  const AY = new THREE.Vector3(0, 1, 0);
  const ca = new THREE.Color(theme.grass.colors[0]);
  const cb = new THREE.Color(theme.grass.colors[1]);
  const C = new THREE.Color();
  const out = {
    count: 0,
    region(rx: number, ry: number, size: number, seed0: number): THREE.InstancedMesh[] {
      let seed = (seed0 % 2147483646) + 1;
      const rnd = (): number => {
        seed = (seed * 16807) % 2147483647;
        return seed / 2147483647;
      };
      const buckets = new Map<string, { x: number; y: number; s: number; yaw: number; c: number }[]>();
      const xa = Math.max(gx0, rx);
      const ya = Math.max(gy0, ry);
      const xb = Math.min(gx1, rx + size);
      const yb = Math.min(gy1, ry + size);
      for (let y = ya; y < yb; y += step)
        for (let x = xa; x < xb; x += step) {
          const px = x + rnd() * step;
          const py = y + rnd() * step;
          if (px >= xb || py >= yb) continue;
          const e = sampleGrid(G, L.sdf, px, py);
          if (e > 9) continue;
          const pw = sampleGrid(G, L.path, px, py);
          const pz = sampleGrid(G, L.plaza, px, py);
          const clump = vnoise(px * 0.18, py * 0.18);
          // +zone: biomes thin or thicken the grass; none under water.
          if (L.terrain && heightAt(px, py) < -0.15) continue;
          const keep = (1 - pw * 0.95) * (1 - pz) * (0.35 + 0.9 * clump) * (e > 2 ? 1 - (e - 2) / 8 : 1) * L.look(px, py).grassK;
          if (rnd() > keep) continue;
          if (L.blocked(px, py)) continue;
          const key = `${Math.floor(px / CHUNK)}|${Math.floor(py / CHUNK)}`;
          let l = buckets.get(key);
          if (!l) buckets.set(key, (l = []));
          l.push({ x: px, y: py, s: 0.75 + rnd() * 0.6 + clump * 0.3, yaw: rnd() * Math.PI * 2, c: rnd() });
        }
      const meshes: THREE.InstancedMesh[] = [];
      for (const list of buckets.values()) {
        const im = new THREE.InstancedMesh(geo, mat, list.length);
        list.forEach((t, i) => {
          M4.compose(V.set(t.x, heightAt(t.x, t.y), -t.y), Q.setFromAxisAngle(AY, t.yaw), S.set(t.s, t.s * (0.85 + t.c * 0.4), t.s));
          im.setMatrixAt(i, M4);
          C.copy(ca).lerp(cb, t.c);
          if (groundColorAt) {
            // +valley: the tuft is the ground's own colour (lighter tips via the vertex ramp).
            const g = groundColorAt(t.x, t.y);
            C.setRGB(C.r * g[0] * 1.15, C.g * g[1] * 1.12, C.b * g[2] * 1.08);
          }
          im.setColorAt(i, C);
        });
        im.computeBoundingSphere();
        im.receiveShadow = true;
        im.castShadow = false;
        im.userData['noStyle'] = true;
        im.name = 'grass';
        meshes.push(im);
        out.count += list.length;
      }
      return meshes;
    },
    dispose() {
      geo.dispose();
      mat.dispose();
    },
  };
  return out;
}

function pointInPolyFast(x: number, y: number, pts: readonly { x: number; y: number }[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i]!;
    const b = pts[j]!;
    if (a.y > y !== b.y > y && x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

// ---------------------------------------------------------------------------------------------
// Sky + textures
// ---------------------------------------------------------------------------------------------

// +atmos: the sky dome moved to atmosphere/sky.ts.

/** Painted grass detail: soft greyscale strokes (vertex colours carry the hue). */
function makeGrassTexture(maxAniso: number): THREE.CanvasTexture | null {
  if (typeof document === 'undefined') return null;
  const S = 256;
  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  const g = cv.getContext('2d')!;
  g.fillStyle = 'rgb(232,232,232)';
  g.fillRect(0, 0, S, S);
  let seed = 7;
  const r = (): number => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  // Broad soft blotches, then short blade strokes (wrapped so the tile is seamless).
  for (let i = 0; i < 40; i++) {
    const x = r() * S;
    const y = r() * S;
    const rad = 14 + r() * 30;
    const v = 200 + r() * 55;
    for (const [ox, oy] of [[0, 0], [S, 0], [-S, 0], [0, S], [0, -S]] as const) {
      const grd = g.createRadialGradient(x + ox, y + oy, 0, x + ox, y + oy, rad);
      grd.addColorStop(0, `rgba(${v},${v},${v},0.35)`);
      grd.addColorStop(1, `rgba(${v},${v},${v},0)`);
      g.fillStyle = grd;
      g.fillRect(x + ox - rad, y + oy - rad, rad * 2, rad * 2);
    }
  }
  g.lineCap = 'round';
  for (let i = 0; i < 1400; i++) {
    const x = r() * S;
    const y = r() * S;
    const len = 3 + r() * 6;
    const a = -Math.PI / 2 + (r() - 0.5) * 1.2;
    const v = r() < 0.5 ? 175 + r() * 40 : 245 + r() * 10;
    g.strokeStyle = `rgba(${v},${v},${v},0.55)`;
    g.lineWidth = 1 + r() * 1.2;
    for (const [ox, oy] of [[0, 0], [S, 0], [-S, 0], [0, S], [0, -S]] as const) {
      g.beginPath();
      g.moveTo(x + ox, y + oy);
      g.lineTo(x + ox + Math.cos(a) * len, y + oy + Math.sin(a) * len);
      g.stroke();
    }
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = Math.min(8, maxAniso);
  return tex;
}

/** Cobblestones: rounded stones in staggered rows over dark mortar (tileable). */
function makeCobbleTexture(maxAniso: number): THREE.CanvasTexture | null {
  if (typeof document === 'undefined') return null;
  const S = 512;
  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  const g = cv.getContext('2d')!;
  g.fillStyle = 'rgb(92,86,78)';
  g.fillRect(0, 0, S, S);
  let seed = 11;
  const r = (): number => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  const rows = 8;
  const rh = S / rows;
  for (let row = 0; row < rows; row++) {
    let x = row % 2 ? -rh * 0.5 : 0;
    while (x < S) {
      const w = rh * (0.8 + r() * 0.6);
      const v = 180 + r() * 50;
      const warm = r() * 14;
      for (const ox of [0, S, -S]) {
        const cx = x + ox + 3;
        const cy = row * rh + 3;
        const ww = w - 6;
        const hh = rh - 6;
        g.fillStyle = `rgb(${v + warm},${v + warm * 0.5},${v - warm * 0.3})`;
        roundRect(g, cx, cy, ww, hh, 10);
        g.fill();
        g.fillStyle = 'rgba(255,255,255,0.14)';
        roundRect(g, cx + 3, cy + 2, ww - 6, hh * 0.35, 8);
        g.fill();
        g.fillStyle = 'rgba(0,0,0,0.12)';
        roundRect(g, cx + 2, cy + hh * 0.7, ww - 4, hh * 0.3, 8);
        g.fill();
      }
      x += w;
    }
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = Math.min(8, maxAniso);
  return tex;
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}
